import { useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { Button, Input } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { PlacementFormValue } from '../../placementFormSchema';

export function PlacementImpressionTrackersFields() {
  'use no memo';
  const { control, register, formState } = useFormContext<PlacementFormValue>();
  const { fields, append, remove } = useFieldArray({ control, name: 'impressionTrackers' });

  return (
    <section className="border-input overflow-hidden rounded-md border">
      <header
        className={`border-input flex items-center justify-between px-4 py-3 ${fields.length ? 'border-b' : ''}`}
      >
        <h2 className="text-sm font-semibold">{dashboardT('placements.impressionTrackers')}</h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            append({ tracker: '', sampleRate: '' });
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {dashboardT('placements.addTracker')}
        </Button>
      </header>
      {fields.length ? (
        <div className="divide-border divide-y">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="grid gap-3 p-4 sm:grid-cols-[minmax(0,1fr)_180px_36px] sm:items-end"
            >
              <DashboardPageFormField
                label={dashboardT('placements.trackerUrl')}
                error={formState.errors.impressionTrackers?.[index]?.tracker?.message}
              >
                <Input {...register(impressionTrackerPath(index, 'tracker'))} />
              </DashboardPageFormField>
              <DashboardPageFormField
                label={dashboardT('placements.sampleRate')}
                error={formState.errors.impressionTrackers?.[index]?.sampleRate?.message}
              >
                <Input
                  type="number"
                  min="0"
                  max="100"
                  {...register(impressionTrackerPath(index, 'sampleRate'))}
                />
              </DashboardPageFormField>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={dashboardT('placements.removeTracker')}
                onClick={() => {
                  remove(index);
                }}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function impressionTrackerPath<TField extends 'tracker' | 'sampleRate'>(
  index: number,
  field: TField,
): `impressionTrackers.${number}.${TField}` {
  // RHF models field-array indexes as numeric template-literal paths.
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return `impressionTrackers.${index}.${field}`;
}
